import { fobiCore } from '../../FobiCore';
import { parseEFNEingabe, parseEinzelneEFNMitBeschriftung, istGleicherNameStreng,
	bereinigeBeschriftung } from './efnBarcodeContentRecogniser';

/**
 * Das Eingabefeld zeilenweise lesen: **eine Zeile = eine EFN + ihr Name**.
 *
 * Solange dort ein <input type="text"> stand, war das nicht moeglich - ein Textfeld nimmt keine
 * Zeilenumbrueche an, eine eingefuegte Excel-Liste kam als eine einzige Zeile an, und deshalb
 * konnte immer nur *eine* EFN einen Namen bekommen (parseEinzelneEFNMitBeschriftung). Mit einer
 * Textarea bleibt die Zeilenstruktur erhalten, und genau sie traegt die Zuordnung.
 *
 * Die zweite Idee: **uebernommen wird, was geht; der Rest bleibt stehen.** Eine vertippte EFN
 * unter vierzig guten sperrt nicht mehr alles - sie wird nicht uebernommen, bleibt sichtbar im
 * Feld stehen und verlangt weiter eine Entscheidung. Das leere Feld ist die Quittung.
 *
 * Aufgebaut auf efnBarcodeContentRecogniser, nie umgekehrt - der Basisparser darf von hier
 * nichts wissen, sonst importieren sich beide Module gegenseitig.
 */

const NBSP = /[  ]/g;
const ZERO_WIDTH = /[​-‍﻿]/g;

/** Word und Excel liefern geschuetzte und nullbreite Leerzeichen mit; sie stoeren jeden Vergleich. */
const normalisiere = (eingabe) => (typeof eingabe === 'string' ? eingabe : '')
	.replace(NBSP, ' ')
	.replace(ZERO_WIDTH, '');

/**
 * Word nummeriert und bulletet Listen, Excel stellt gern eine Indexspalte voran. Beides steht
 * am Zeilenanfang und ist sicher kein Name - alles andere bleibt unangetastet.
 */
const LISTENMARKER = /^\s*(\d{1,3}[.)]|[•·*–—-])\s+/;
const INDEXSPALTE = /^\s*\d{1,3}\t/;

const entferneListenmarker = (zeile) => {
	const ohneMarker = zeile.replace(LISTENMARKER, '');
	if(!INDEXSPALTE.test(ohneMarker)) return ohneMarker;
	// Die Indexspalte faellt nur, wenn danach noch eine EFN steht - sonst schnitten wir
	// moeglicherweise das Einzige weg, was die Zeile ausmacht.
	const ohneIndex = ohneMarker.replace(INDEXSPALTE, '');
	return fobiCore.findEFNInText(ohneIndex) ? ohneIndex : ohneMarker;
};

/**
 * Aus 8,02761E+14 ist keine EFN zu rekonstruieren - Excel hat die Stellen bereits verworfen.
 * Melden, nicht raten; der Rat ist derselbe wie beim CSV-Export: Spalte als Text formatieren.
 */
const EXPONENTIALSCHREIBWEISE = /\d[.,]\d+E[+-]?\d+/i;

/**
 * Eine **Tabellenzeile** erkennt man daran, dass die EFN allein in einer Spalte steht - so
 * kommt es aus Excel, und so kam auch der Fall aus der Praxis herein:
 *
 *   802760902471597 | - | Tamar Andguladze | 2 | EIV | - | 2 | 0 | 0
 *
 * Als Wortfolge gelesen sind das zehn Woerter, die Namensgrenze greift, und die ganze Zeile
 * bleibt liegen - obwohl die EFN voellig eindeutig ist. Spaltenweise gelesen ist sie einfach:
 * eine EFN-Spalte, ein paar Zahlenspalten, und Spalten mit Buchstaben sind die Kandidaten fuer
 * den Namen. Gibt es genau einen, ist es der Name; gibt es mehrere ("Tamar Andguladze" und
 * "EIV"), wird **die EFN ohne Namen uebernommen** und die Vorschau sagt es an. Geraten wird
 * nicht - aber die Teilnahme geht auch nicht verloren, nur weil eine Notiz unklar ist.
 *
 * Die Bedingung "EFN allein in einer Spalte" ist bewusst eng: in einer eingefuegten Kopfzeile
 * klebt die EFN am Nachbartext ("Meldedatum802760600165921"), dort greift sie nicht, und die
 * Zeile bleibt wie bisher liegen.
 */
// \p{L} statt [A-Za-z...]: kyrillische, griechische und polnische Namen sind Namen. Mit einer
// Latin-1-Klasse fiel eine solche Spalte lautlos aus der Kandidatenmenge, und die EFN wurde
// ohne Namen uebernommen.
const HAT_BUCHSTABEN = /\p{L}/u;

/**
 * Tab kommt aus Excel und Word, Semikolon aus einer CSV-Datei - auch aus unserer eigenen
 * (Druckansicht, "Als CSV speichern"), die jemand auf einem zweiten Erfassungsgeraet wieder
 * einfuegt, um die Namen zu bekommen.
 */
const trennerVon = (zeile) => zeile.indexOf('\t') >= 0 ? '\t' : (zeile.indexOf(';') >= 0 ? ';' : null);

/**
 * CSV setzt Werte in Anfuehrungszeichen und verdoppelt innere - und **genau darum darf nicht
 * einfach an jedem Semikolon getrennt werden**: der eigene Export (`zuCsv`) laesst ein Semikolon
 * im Namen zu, es steht dann innerhalb der Anfuehrungszeichen. Ein split(';') zerschnitte
 * `"Praxis ""Am Markt""; Bauer"` in zwei Spalten, und der Name kaeme abgeschnitten zurueck.
 *
 * Ein Anfuehrungszeichen eroeffnet ein Feld nur an dessen Anfang. Mitten im Text getippt
 * (`Praxis "Am Markt"`, nicht aus einer CSV) bleibt es ein gewoehnliches Zeichen.
 *
 * Zeilenumbrueche innerhalb eines Feldes gibt es hier nicht: die Namen kommen aus einem
 * einzeiligen Feld, und die Zeile ist bereits die Einheit.
 */
const zerlegeSpalten = (zeile, trenner) => {
	const spalten = [];
	let feld = '';
	let inAnfuehrung = false;
	for(let i = 0; i < zeile.length; i++){
		const zeichen = zeile[i];
		if(inAnfuehrung){
			if(zeichen !== '"') feld += zeichen;
			else if(zeile[i + 1] === '"'){ feld += '"'; i++; }
			else inAnfuehrung = false;
		}
		else if(zeichen === '"' && !feld.trim()){ feld = ''; inAnfuehrung = true; }
		else if(zeichen === trenner){ spalten.push(feld.trim()); feld = ''; }
		else feld += zeichen;
	}
	spalten.push(feld.trim());
	return spalten;
};

const spaltenVon = (zeile) => {
	const trenner = trennerVon(zeile);
	return trenner ? zerlegeSpalten(zeile, trenner) : undefined;
};

const alsTabellenzeile = (zeile, efns, email) => {
	// Nur Tabs erkennen von sich aus Spalten. Bei Semikolon bliebe sonst "802...;Mustermann;
	// Oleg;mail" ohne Namen, weil zwei Spalten Buchstaben tragen - als Wortfolge gelesen ist es
	// dagegen eindeutig. Semikolonspalten werden erst benutzt, wenn eine Kopfzeile sagt, welche
	// die Namensspalte ist (siehe uebernehmeKopfzeile).
	if(zeile.indexOf('\t') < 0 || efns.length !== 1) return null;
	const spalten = spaltenVon(zeile);
	if(spalten.filter(spalte => fobiCore.isEFN(spalte)).length !== 1) return null;
	const istKandidat = (spalte) => Boolean(spalte)
		&& !fobiCore.isEFN(spalte)
		&& HAT_BUCHSTABEN.test(spalte)
		&& !(email && spalte.indexOf(email) >= 0);
	return {
		kandidaten: spalten.filter(istKandidat),
		kandidatenIndizes: spalten.map((spalte, index) => istKandidat(spalte) ? index : -1).filter(index => index >= 0)
	};
};

/** Spaltenkoepfe, die eine Namensspalte ankuendigen - "Name / Notiz" aus unserem eigenen Export inklusive. */
const NAMENS_KOPF = /^(name|notiz|teilnehmer|nachname|vorname)/i;
const VORNAME_KOPF = /^vorname/i;
const NACHNAME_KOPF = /^nachname/i;

/**
 * Steht eine Kopfzeile dabei, muss nichts erschlossen werden: die Datei sagt selbst, welche
 * Spalte der Name ist. Das ist der einzige Weg, auf dem auch Semikolon-Spalten benutzt werden -
 * ohne Kopfzeile bliebe zu viel Raterei.
 *
 * Kopfzeile heisst: eine Zeile mit Spalten, in der keine EFN steht.
 */
const findeNamensspaltenAusKopfzeile = (zeilen) => {
	const kopf = zeilen.find(zeile => zeile.spalten && zeile.efns.length === 0);
	if(!kopf) return [];
	const passende = kopf.spalten
		.map((spalte, index) => ({ index, titel: spalte.trim() }))
		.filter(spalte => NAMENS_KOPF.test(spalte.titel));
	// "Vorname" und "Nachname" in getrennten Spalten ist die verbreitetste Form einer
	// Verwaltungsliste. Nur die erste zu nehmen liesse den halben Namen still fallen - und die
	// Reihenfolge steht hier ausnahmsweise fest, sie steht ja in der Kopfzeile. Ueberall sonst
	// wird bewusst nicht umsortiert ("Nachname, Vorname" ist von aussen nicht zu entscheiden).
	const vorname = passende.find(spalte => VORNAME_KOPF.test(spalte.titel));
	const nachname = passende.find(spalte => NACHNAME_KOPF.test(spalte.titel));
	if(vorname && nachname) return [vorname.index, nachname.index];
	return passende.map(spalte => spalte.index);
};

const uebernehmeKopfzeile = (zeilen) => {
	const namensspalten = findeNamensspaltenAusKopfzeile(zeilen);
	if(namensspalten.length === 0) return false;
	let angewandt = false;
	zeilen.forEach(zeile => {
		if(!zeile.spalten) return;
		if(zeile.spalten.filter(spalte => fobiCore.isEFN(spalte)).length !== 1) return;
		if(zeile.efns.length !== 1) return;
		zeile.status = 'einzelneEFN';
		zeile.text = bereinigeBeschriftung(
			namensspalten.map(index => (zeile.spalten[index] || '').trim()).filter(spalte => spalte).join(' '));
		zeile.hinweise = zeile.hinweise.filter(hinweis =>
			hinweis !== 'nameUneindeutig' && hinweis !== 'ohneName' && hinweis !== 'weitereSpalten');
		if(!zeile.text) zeile.hinweise.push('ohneName');
		angewandt = true;
	});
	return angewandt;
};

/**
 * Eine einzelne Tabellenzeile mit zwei Buchstabenspalten ist nicht entscheidbar - die *Liste*
 * dagegen oft schon, denn eine Tabelle hat in jeder Zeile dieselben Spalten:
 *
 *   EFN | -     | Tamar Andguladze               | 2 | EIV | ...
 *   EFN | 10099 | Dr. medic (R) Hans Egon Doerner
 *
 * Spalte 2 traegt in *jeder* Zeile einen Namenskandidaten, Spalte 4 nur in einer. Ist genau ein
 * Index in allen Tabellenzeilen Kandidat, ist das die Namensspalte. Das ist keine Annahme
 * darueber, wie ein Name aussieht, sondern die Struktur der Tabelle selbst.
 *
 * Bleibt es mehrdeutig - oder ist es nur eine einzige Zeile -, gilt weiter die vorsichtige
 * Regel: EFN ohne Namen, und die Vorschau sagt es an.
 */
const vereinheitlicheNamensspalte = (zeilen) => {
	const tabellenzeilen = zeilen.filter(zeile => zeile.kandidatenIndizes.length > 0);
	if(tabellenzeilen.length < 2) return;
	const gemeinsame = tabellenzeilen[0].kandidatenIndizes.filter(index =>
		tabellenzeilen.every(zeile => zeile.kandidatenIndizes.indexOf(index) >= 0));
	if(gemeinsame.length !== 1) return;
	const namensspalte = gemeinsame[0];
	tabellenzeilen.forEach(zeile => {
		zeile.text = bereinigeBeschriftung(zeile.spalten[namensspalte]);
		zeile.hinweise = zeile.hinweise.filter(hinweis => hinweis !== 'nameUneindeutig' && hinweis !== 'ohneName');
	});
};

/**
 * Fuer Zeilen, die *keine* Tabellenzeile sind: bleiben nach EFN und E-Mail noch zwei oder mehr
 * befuellte Tab-Spalten uebrig, konnte der Wortweg Beifang in den Namen genommen haben.
 */
const zaehleWeitereSpalten = (zeile, efn, email) => {
	if(zeile.indexOf('\t') < 0) return 0;
	return zeile.split('\t')
		.map(feld => feld.trim())
		.filter(feld => feld)
		.filter(feld => !(efn && feld.indexOf(efn) >= 0))
		.filter(feld => !(email && feld.indexOf(email) >= 0))
		.length;
};

const istGanzesJson = (text) => {
	if(!(text.startsWith('{') || text.startsWith('['))) return false;
	try {
		JSON.parse(text);
		return true;
	} catch (e) {
		return false;
	}
};

/**
 * Leere Zeilen zaehlen fuer die Nummerierung mit, kommen aber nicht zurueck - sonst bliebe nach
 * der Uebernahme ein scheinbar leeres Feld voller Zeilenumbrueche stehen.
 *
 * Mehrzeilig formatiertes JSON bleibt **eine** Einheit: der JSON-Zweig in parseEFNEingabe ist
 * bestehende Funktionalitaet und darf an der Zeilentrennung nicht zerbrechen.
 */
export const zerlegeZeilen = (eingabe) => {
	const text = normalisiere(eingabe);
	const getrimmt = text.trim();
	if(!getrimmt) return [];
	if(istGanzesJson(getrimmt)) return [{ nr: 1, roh: getrimmt }];
	return text.split(/\r\n?|\n/)
		.map((roh, index) => ({ nr: index + 1, roh: roh.trim() }))
		.filter(zeile => zeile.roh);
};

/**
 * Der Status einer Zeile. **Die Reihenfolge ist Teil der Regel**, nicht nur eine Formulierung:
 *
 * - `efns.length > 0` steht vor validateMultipleEFNs, weil dieses mit every(isEFN) prueft und
 *   [].every(...) wahr ist - reiner Text haette sonst als "lauter EFNs" gegolten. Genau diese
 *   Wache stand vorher in istGueltigeEFNEingabe.
 * - Nicht "eine EFN also einzelneEFN": "802760600165921 4711000815" hat genau eine EFN und ist
 *   trotzdem ungueltig, weil die zweite lange Zahl unerklaert bleibt.
 */
const ermittleStatus = (zeile, efns, einzelMitBeschriftung) => {
	if(efns.length > 0 && fobiCore.validateMultipleEFNs(zeile)) return efns.length === 1 ? 'einzelneEFN' : 'mehrereEFNs';
	if(einzelMitBeschriftung) return 'einzelneEFN';
	if(efns.length === 0) return 'ohneEFN';
	return 'ungueltig';
};

export const parseZeile = (roh) => {
	const zeile = entferneListenmarker(roh);
	const efns = fobiCore.extractEFNsToArray(zeile);
	const geparst = parseEFNEingabe(zeile);
	const einzelMitBeschriftung = parseEinzelneEFNMitBeschriftung(zeile);
	const tabelle = alsTabellenzeile(zeile, efns, geparst.email);
	const status = tabelle ? 'einzelneEFN' : ermittleStatus(zeile, efns, einzelMitBeschriftung);
	const hinweise = [];
	if(fobiCore.extractVerdaechtigeEFNsToArray(zeile).length > 0) hinweise.push('verdaechtig');
	if(EXPONENTIALSCHREIBWEISE.test(zeile)) hinweise.push('exponential');
	// Nur fuer Zeilen, die auch eine EFN tragen und *nicht* spaltenweise gelesen wurden - dort
	// ist der Beifang bereits sortiert, der Hinweis waere nur Rauschen.
	if(!tabelle && efns.length > 0 && zaehleWeitereSpalten(zeile, geparst.efn, geparst.email) >= 2) hinweise.push('weitereSpalten');
	if(tabelle && tabelle.kandidaten.length > 1) hinweise.push('nameUneindeutig');
	// Mehrere EFNs *und* ein Name in einer Zeile: welcher Name zu welcher Nummer gehoert, ist
	// nicht zu entscheiden. Die Zeile bleibt deshalb stehen, statt die Namen stillschweigend
	// fallen zu lassen - wer nur die Nummern will, kommt mit "bereinigen" in einem Schritt hin.
	if(status === 'mehrereEFNs' && geparst.text) hinweise.push('mehrereEFNsMitBeifang');
	const text = tabelle
		? (tabelle.kandidaten.length === 1 ? bereinigeBeschriftung(tabelle.kandidaten[0]) : '')
		: (einzelMitBeschriftung ? einzelMitBeschriftung.text : '');
	// Die erkannte Adresse unterdrueckte diesen Hinweis frueher - eine Zeile mit EFN und blosser
	// E-Mail galt als beschriftet. Sie traegt jetzt "ohne Name", was die richtige Aussage ist:
	// ein Name steht dort nicht. Uebernehmbar bleibt sie (das entscheidet hatBeifang).
	if(status === 'einzelneEFN' && !text) hinweise.push('ohneName');
	return {
		roh,
		// Fuer die Nachbearbeitung ueber die ganze Liste (Kopfzeile, gemeinsame Namensspalte).
		spalten: spaltenVon(zeile),
		kandidatenIndizes: tabelle ? tabelle.kandidatenIndizes : [],
		efn: efns.length === 1 ? efns[0] : undefined,
		efns,
		text,
		// Fuer den Datenschutzhinweis: steht in dieser Zeile ueberhaupt etwas, das ein Name sein
		// koennte? Bewusst grosszuegiger als `text` - lieber einmal zu oft daran erinnern, dass
		// Namen im Browser liegen, als einmal zu selten.
		enthaeltNamen: Boolean(geparst.text || geparst.email),
		status,
		hinweise
	};
};

const BLOCKIERENDE_HINWEISE = ['verdaechtig', 'exponential', 'mehrereEFNsMitBeifang'];

/**
 * `uebernehmbar` wird abgeleitet und nicht gespeichert: zwei Wahrheiten in einem Datensatz
 * laufen frueher oder spaeter auseinander.
 */
export const istUebernehmbar = (zeile) =>
	zeile.efns.length > 0
	&& (zeile.status === 'einzelneEFN' || zeile.status === 'mehrereEFNs')
	&& !zeile.hinweise.some(hinweis => BLOCKIERENDE_HINWEISE.indexOf(hinweis) >= 0);

export const bleibtStehen = (zeile) => !istUebernehmbar(zeile);

/**
 * Dieselbe EFN mehrfach in einer Eingabe.
 *
 * **Die Teilnahme wird trotzdem angelegt.** Widersprochen wird dem *Namen*, und der bleibt
 * ohnehin im Browser - wer wegen einer strittigen Notiz die Meldung unterlaesst, verliert den
 * Teilnehmer ganz. Genau so haelt es der Scan-Weg: zweimal denselben Ausweis gescannt legt die
 * Teilnahme an und merkt den zweiten Namen als `abweichenderScan`; aufgeloest wird er spaeter
 * in der Zeile mit ergaenzen / ersetzen / verwerfen. Zweimal dieselbe Liste einzufuegen muss
 * dasselbe tun.
 *
 * `widerspruch` ist deshalb ein Hinweis, keine Sperre - er sagt in der Vorschau vorher, was
 * gleich passiert.
 *
 * Verglichen wird **paarweise** und **streng**: "M. Mustermann", "Max Mustermann" und
 * "Moritz Mustermann" waeren sonst ueber die Bruecke "M." eine Gruppe.
 */
const markiereMehrfachvorkommen = (zeilen) => {
	const nachEFN = new Map();
	zeilen.forEach(zeile => zeile.efns.forEach(efn => {
		if(!nachEFN.has(efn)) nachEFN.set(efn, []);
		nachEFN.get(efn).push(zeile);
	}));
	nachEFN.forEach((zeilenZurEFN) => {
		if(zeilenZurEFN.length < 2) return;
		zeilenZurEFN.forEach(zeile => {
			if(zeile.hinweise.indexOf('mehrfach') < 0) zeile.hinweise.push('mehrfach');
		});
		const namen = zeilenZurEFN.map(zeile => zeile.text).filter(name => name);
		const namenWidersprechen = namen.some((name, stelle) =>
			namen.slice(stelle + 1).some(anderer => !istGleicherNameStreng(name, anderer)));
		if(namenWidersprechen){
			// An allen Zeilen dieser EFN, auch der unbeschrifteten - der Widerspruch gehoert zur
			// Nummer, nicht zur einzelnen Zeile.
			zeilenZurEFN.forEach(zeile => {
				if(zeile.hinweise.indexOf('widerspruch') < 0) zeile.hinweise.push('widerspruch');
			});
		}
	});
};

export const parseListe = (eingabe) => {
	const zeilen = zerlegeZeilen(eingabe).map(({ nr, roh }) => ({ nr, ...parseZeile(roh) }));
	// Erst die Namensspalte ueber die ganze Liste bestimmen, dann die Widersprueche suchen -
	// die brauchen die endgueltigen Namen.
	// Die Kopfzeile hat Vorrang - sie ist eine Auskunft, keine Vermutung.
	if(!uebernehmeKopfzeile(zeilen)) vereinheitlicheNamensspalte(zeilen);
	markiereMehrfachvorkommen(zeilen);
	return { zeilen };
};

/**
 * Die Beschriftungen, die uebernommen werden - in Eingabereihenfolge, mehrere je EFN moeglich.
 *
 * Zwei Schritte, beide noetig:
 *
 * 1. **Kompatible Namen zusammenfassen** ("O. Mustermann" / "Oleg Mustermann"), der laengere
 *    gewinnt. Ohne das behielte uebernehmeBeschriftungAusScan den zuerst gesehenen, also den
 *    kuerzeren - es laesst bei kompatiblen Namen den vorhandenen stehen. Geprueft wird streng
 *    und gegen *alle* Mitglieder einer Gruppe, sonst waeren "M.", "Max" und "Moritz" ueber die
 *    Bruecke "M." eine Person.
 * 2. **Wirklich verschiedene Namen bleiben getrennt** und gehen nacheinander durch den Merge -
 *    dort entsteht daraus derselbe `abweichenderScan` wie bei zwei Scans desselben Ausweises.
 */
export const kanonischeBeschriftungen = (zeilen) => {
	const nachEFN = new Map();
	zeilen.filter(istUebernehmbar).forEach(zeile => {
		if(!zeile.text) return;
		zeile.efns.forEach(efn => {
			if(!nachEFN.has(efn)) nachEFN.set(efn, []);
			const gruppen = nachEFN.get(efn);
			const passende = gruppen.find(gruppe =>
				gruppe.namen.every(name => istGleicherNameStreng(name, zeile.text)));
			if(!passende){
				gruppen.push({ efn, text: zeile.text, namen: [zeile.text] });
				return;
			}
			passende.namen.push(zeile.text);
			if(zeile.text.length > passende.text.length) passende.text = zeile.text;
		});
	});
	return [...nachEFN.values()]
		.reduce((alle, gruppen) => alle.concat(gruppen), [])
		.map(({ efn, text }) => ({ efn, text }));
};

/** Alle EFNs, die tatsaechlich uebernommen werden - ohne Doppelte. */
export const uebernehmbareEFNs = (zeilen) =>
	[...new Set(zeilen.filter(istUebernehmbar).reduce((alle, zeile) => alle.concat(zeile.efns), []))];

/**
 * "bereinigen" und "ungueltige entfernen" arbeiten **je Zeile**. Ohne das machte ein Klick aus
 * einer vierzigzeiligen Liste wieder eine einzige Zeile - und damit waere die Zuordnung
 * Name -> EFN weg, also genau das, wofuer die Zeilen da sind.
 */
const jeZeile = (text, abbildung) => (typeof text === 'string' ? text : '')
	.split(/\r\n?|\n/)
	.map(abbildung)
	.map(zeile => zeile.trim())
	.filter(zeile => zeile)
	.join('\n');

/** Namen, Datumsangaben und PLZ raus, Nummern bleiben - zeilenweise. */
export const entferneFremdinhaltJeZeile = (text) => jeZeile(text, fobiCore.removeFremdinhalt);

/** Nur noch gueltige EFNs, verdaechtige Nummern fallen - zeilenweise. */
export const nurEFNsJeZeile = (text) => jeZeile(text, zeile => fobiCore.extractEFNsToArray(zeile).join(' '));

/** Steht in der Eingabe ueberhaupt etwas, das ein Name sein koennte? Fuer den Datenschutzhinweis. */
export const enthaeltNamen = (eingabe) => parseListe(eingabe).zeilen.some(zeile => zeile.enthaeltNamen);

/** Was im Feld stehen bleibt: unveraendert, so wie es der Anwender hingeschrieben hat. */
export const restText = (zeilen) => zeilen.filter(bleibtStehen).map(zeile => zeile.roh).join('\n');

/**
 * Die eine Regel, ob die Eingabe uebernommen werden darf - fuer den Knopf *und* fuer die Wache
 * im Thunk. Vorher liess die Komponente mehr zu als der Reducer: bei "<EFN> Praxis 2" war
 * "Hinzufuegen" aktiv, die Teilnahme landete aber nirgends. Nach aussen sah das aus, als
 * passiere nichts - waehrend die Beschriftung trotzdem geschrieben wurde.
 */
export const istGueltigeEFNEingabe = (eingabe) => parseListe(eingabe).zeilen.some(istUebernehmbar);

/**
 * Einzelmodus ist der Weg an der Tuer: eine EFN, Enter, die naechste. Nur hier darf eine bereits
 * erfasste Teilnahme aktualisiert werden - das ist der Korrekturweg. Bei einer Liste bleiben
 * vorhandene Teilnahmen unangetastet, sonst planierte ein Paste ueber vierzig Zeilen die
 * individuell gesetzten Punkte.
 *
 * Gezaehlt werden die *logischen* Zeilen aus zerlegeZeilen, nicht die Umbrueche des Rohtexts -
 * ein mehrzeilig formatiertes JSON mit einer EFN ist ein Einzelvorgang. Und die Bindung an
 * status/efns.length statt an "eine verschiedene EFN" ist Absicht: extractEFNsToArray
 * dedupliziert nicht, "<EFN> <dieselbe EFN>" ist mehrereEFNs und damit Listenmodus.
 */
export const istEinzelmodus = (eingabe) => {
	const { zeilen } = parseListe(eingabe);
	if(zeilen.length !== 1) return false;
	const zeile = zeilen[0];
	return zeile.status === 'einzelneEFN' && zeile.efns.length === 1 && istUebernehmbar(zeile);
};

/**
 * Eine einzelne Ziffernfolge, die noch zu einer EFN werden kann: kein Fehler, sondern Tippen.
 *
 * Bis hierher bekam jeder Zwischenstand einer von Hand getippten EFN den vollen Apparat des
 * Listenmodus - roter Rahmen, Vorschautabelle, "ungueltige entfernen". Vierzehn Alarme fuer einen
 * Vorgang, und ein Knopf, der anbietet, das wegzuwerfen, was gerade entsteht. Verdient ist die
 * Vorschau dort, wo das Ergebnis nicht zu ueberblicken ist: vierzig Zeilen aus Excel.
 *
 * **Bewusst ohne Praefixpruefung.** EFNs beginnen mit 8, "7..." kann also nie mehr eine werden -
 * das aber schon nach der ersten Ziffer zu sagen, waere genau der Laerm, um den es geht. Wer sich
 * vertippt, korrigiert, statt weiterzutippen.
 *
 * **Erst Zeilenumbrueche ausschliessen, dann trimmen.** `trim()` entfernt auch \r und \n - in der
 * umgekehrten Reihenfolge gaelte "12345\n" als eine Zeile. Sobald eine zweite Zeile im Spiel ist,
 * ist es eine Liste. Geprueft wird der **Rohtext**, damit die Regel nicht davon abhaengt, wie der
 * Parser eine Ziffernfolge zerlegt.
 */
export const istEFNImWerden = (eingabe) => {
	const rohtext = typeof eingabe === 'string' ? eingabe : '';
	if(/[\r\n]/.test(rohtext)) return false;
	return /^\d{1,14}$/.test(rohtext.trim());
};

export const listenEingabe = { parseListe, parseZeile, zerlegeZeilen, istUebernehmbar, bleibtStehen,
	kanonischeBeschriftungen, uebernehmbareEFNs, restText, istGueltigeEFNEingabe, istEinzelmodus,
	istEFNImWerden, entferneFremdinhaltJeZeile, nurEFNsJeZeile, enthaeltNamen };
